import { useState, useRef, useEffect } from "react";
import {
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import { When } from "react-if";
import { useDebounce } from "../../../../hooks/useDebounce";
import { ComposeSearchService } from "../../../../services/compose-search.service";
import { BrowseGroup, SearchGroupItem, SearchTeamItem, SearchUserItem } from "../../../../services/compose-search.types";
import { GT_COLORS } from "../../../../constants/designTokens.constants";
import { displayName } from "../../../../helpers/displayName";

interface Props {
    groupIds: string[];
    teamIds: string[];
    userIds: string[];
    selectAllUsers: boolean;
    browseData: BrowseGroup[];
    // True while the browse tree is still loading, so an unresolved chip can say
    // "Loading…" rather than asserting the name is unavailable.
    browseLoading?: boolean;
    // True while saved recipient ids are being resolved to names, so their
    // chips say "Loading…" rather than "Name unavailable" during the fetch.
    userNamesLoading?: boolean;
    userDetailsMap: Map<string, SearchUserItem>;
    onRemoveGroup: (groupId: string) => void;
    onRemoveTeam: (teamId: string) => void;
    onRemoveUser: (userId: string) => void;
    onDeselectAll: () => void;
    recipientCount: number | null;
    activeFilter: 'active' | 'inactive' | 'all';
    onSelectGroup: (groupId: string) => void;
    onSelectTeam: (teamId: string) => void;
    onSelectUser: (user: SearchUserItem) => void;
}

export const SelectedRecipientsDisplay: React.FC<Props> = ({
    groupIds,
    teamIds,
    userIds,
    selectAllUsers,
    browseData,
    browseLoading = false,
    userNamesLoading = false,
    userDetailsMap,
    onRemoveGroup,
    onRemoveTeam,
    onRemoveUser,
    onDeselectAll,
    recipientCount,
    activeFilter,
    onSelectGroup,
    onSelectTeam,
    onSelectUser,
}) => {
    const isEmpty = !selectAllUsers && groupIds.length === 0 && teamIds.length === 0 && userIds.length === 0;

    // --- Search state ---
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState<{
        groups: SearchGroupItem[];
        teams: SearchTeamItem[];
        users: SearchUserItem[];
    }>({ groups: [], teams: [], users: [] });
    const [searchLoading, setSearchLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const debouncedQuery = useDebounce(query, 300);

    // Search on debounced query
    useEffect(() => {
        if (!debouncedQuery || debouncedQuery.length < 1) {
            setSearchResults({ groups: [], teams: [], users: [] });
            return;
        }

        setSearchLoading(true);
        const params = { q: debouncedQuery, limit: 10, offset: 0, activeFilter };

        Promise.all([
            ComposeSearchService.searchGroups(params),
            ComposeSearchService.searchTeams(params),
            ComposeSearchService.searchUsers(params),
        ])
            .then(([groups, teams, users]) => {
                setSearchResults({
                    groups: groups.items,
                    teams: teams.items,
                    users: users.items,
                });
                setShowDropdown(true);
            })
            .catch(() => {
                setSearchResults({ groups: [], teams: [], users: [] });
            })
            .finally(() => setSearchLoading(false));
    }, [debouncedQuery, activeFilter]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const hasResults =
        searchResults.groups.length > 0 ||
        searchResults.teams.length > 0 ||
        searchResults.users.length > 0;

    // searchResults is transient — picking a recipient clears the query, and the
    // debounced effect wipes the results ~300ms later. Retain any name we've seen
    // so a recipient chosen via search stays named.
    const seenNames = useRef(new Map<string, string>());

    useEffect(() => {
        for (const g of searchResults.groups) seenNames.current.set(g.id, g.name);
        for (const t of searchResults.teams) seenNames.current.set(t.id, t.name);
        for (const u of searchResults.users) seenNames.current.set(u.id, u.name);
    }, [searchResults]);

    // --- Name / count lookups ---

    // These three never fall back to the id. A recipient chip labelled with a
    // bare ObjectId reads as corrupted data, and the lookups miss for ordinary
    // reasons — browseData still loading, or a recipient selected via search
    // whose group/team isn't in the browse tree.
    const getGroupName = (groupId: string): string => {
        const fromBrowse = browseData.find(g => g.id === groupId)?.name;
        return displayName(fromBrowse || seenNames.current.get(groupId), browseLoading);
    };

    const getGroupUserCount = (groupId: string): number => {
        const group = browseData.find(g => g.id === groupId);
        return group ? group.teams.reduce((sum, t) => sum + t.userCount, 0) : 0;
    };

    const getTeamName = (teamId: string): string => {
        for (const group of browseData) {
            const team = group.teams.find(t => t.id === teamId);
            // Only claim the hit if it actually carries a name (Team.name is
            // optional) — otherwise fall through to the retained search names
            // rather than returning the literal string "undefined".
            if (team?.name) return team.name;
        }
        return displayName(seenNames.current.get(teamId), browseLoading);
    };

    const getTeamUserCount = (teamId: string): number => {
        for (const group of browseData) {
            const team = group.teams.find(t => t.id === teamId);
            if (team) return team.userCount;
        }
        return 0;
    };

    const getUserName = (userId: string): string => {
        const fromMap = userDetailsMap.get(userId)?.name;
        return displayName(fromMap || seenNames.current.get(userId), browseLoading || userNamesLoading);
    };

    const countLabel = (count: number): string => {
        if (activeFilter === 'all') return `${count} users`;
        return `${count} ${activeFilter === 'active' ? 'mobile app' : 'no mobile app'} users`;
    };

    const selectAllLabel = (): string => {
        const filterWord = activeFilter === 'active' ? 'Mobile App: Yes ' : activeFilter === 'inactive' ? 'Mobile App: No ' : '';
        const countSuffix = recipientCount ? ` (${recipientCount})` : ' (*)';
        return `All ${filterWord}Users${countSuffix}`;
    };

    // --- Chip style ---
    const chipSx = {
        backgroundColor: GT_COLORS.primaryLight,
        color: GT_COLORS.primary,
        '& .MuiChip-icon': { color: GT_COLORS.primary },
        '& .MuiChip-deleteIcon': { color: GT_COLORS.primary, '&:hover': { color: GT_COLORS.primaryHover } },
    };

    // --- Selection handlers ---
    const handleSelectGroup = (groupId: string) => {
        onSelectGroup(groupId);
        setQuery('');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSelectTeam = (teamId: string) => {
        onSelectTeam(teamId);
        setQuery('');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSelectUser = (user: SearchUserItem) => {
        onSelectUser(user);
        setQuery('');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    return (
        <Box ref={containerRef} sx={{ position: 'relative', mb: 3 }}>
            <Box
                sx={{
                    minHeight: 56,
                    maxHeight: 200,
                    overflowY: 'auto',
                    border: showDropdown ? `2px solid ${GT_COLORS.primary}` : (isEmpty ? `1px solid ${GT_COLORS.border}` : `2px solid ${GT_COLORS.primary}`),
                    borderRadius: 1,
                    backgroundColor: isEmpty ? 'transparent' : GT_COLORS.primaryLight,
                    p: 1.5,
                    cursor: 'text',
                }}
                onClick={() => inputRef.current?.focus()}
            >
                <Box sx={{ display: 'flex', alignItems: isEmpty ? 'center' : 'flex-start', gap: 1 }}>
                    <SearchIcon sx={{ color: isEmpty && !showDropdown ? GT_COLORS.textFaint : GT_COLORS.primary, mt: isEmpty ? 0 : '4px', flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1, alignItems: 'center' }}>
                        {/* Select All chip */}
                        {selectAllUsers && (
                            <Chip
                                icon={<SelectAllIcon />}
                                label={selectAllLabel()}
                                onDelete={onDeselectAll}
                                sx={chipSx}
                            />
                        )}

                        {/* Group chips */}
                        {!selectAllUsers && groupIds.map(groupId => (
                            <Chip
                                key={`group-${groupId}`}
                                icon={<FolderIcon />}
                                label={`${getGroupName(groupId)} (${countLabel(getGroupUserCount(groupId))})`}
                                onDelete={() => onRemoveGroup(groupId)}
                                sx={chipSx}
                            />
                        ))}

                        {/* Team chips */}
                        {!selectAllUsers && teamIds.map(teamId => (
                            <Chip
                                key={`team-${teamId}`}
                                icon={<GroupIcon />}
                                label={`${getTeamName(teamId)} (${countLabel(getTeamUserCount(teamId))})`}
                                onDelete={() => onRemoveTeam(teamId)}
                                sx={chipSx}
                            />
                        ))}

                        {/* User chips */}
                        {!selectAllUsers && userIds.map(userId => (
                            <Chip
                                key={`user-${userId}`}
                                icon={<PersonIcon />}
                                label={getUserName(userId)}
                                onDelete={() => onRemoveUser(userId)}
                                sx={chipSx}
                            />
                        ))}

                        {/* Inline search input */}
                        <Box
                            component="input"
                            ref={inputRef}
                            value={query}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setQuery(e.target.value);
                                if (e.target.value.length >= 1) setShowDropdown(true);
                            }}
                            onFocus={() => {
                                if (hasResults) setShowDropdown(true);
                            }}
                            disabled={selectAllUsers}
                            placeholder={isEmpty ? 'Search groups, teams, or users...' : ''}
                            sx={{
                                border: 'none',
                                outline: 'none',
                                flex: 1,
                                minWidth: 120,
                                fontSize: '0.875rem',
                                color: GT_COLORS.textPrimary,
                                backgroundColor: 'transparent',
                                p: 0,
                                py: isEmpty ? '7px' : '2.5px',
                                '&::placeholder': {
                                    color: GT_COLORS.textFaint,
                                    opacity: 1,
                                },
                            }}
                        />

                        {searchLoading && (
                            <CircularProgress size={18} sx={{ flexShrink: 0, color: GT_COLORS.primary }} />
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Search Results Dropdown */}
            <When condition={showDropdown && hasResults}>
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        maxHeight: 350,
                        overflow: 'auto',
                        mt: 0.5,
                        borderRadius: 2.5,
                        border: `1px solid ${GT_COLORS.border}`,
                    }}
                >
                    {/* Groups Section */}
                    <When condition={searchResults.groups.length > 0}>
                        <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: GT_COLORS.headerBg }}>
                            Groups
                        </Typography>
                        {searchResults.groups.map((group) => (
                            <ListItemButton
                                key={`group-${group.id}`}
                                onClick={() => handleSelectGroup(group.id)}
                                dense
                                sx={{ '&:hover': { backgroundColor: GT_COLORS.hoverBg } }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Checkbox
                                        size="small"
                                        checked={groupIds.includes(group.id)}
                                        tabIndex={-1}
                                        disableRipple
                                        sx={{ '&.Mui-checked': { color: GT_COLORS.primary } }}
                                    />
                                </ListItemIcon>
                                <FolderIcon sx={{ mr: 1, color: GT_COLORS.textMuted, fontSize: '1rem' }} />
                                <ListItemText
                                    primary={group.name}
                                    secondary={`${group.userCount} users`}
                                />
                            </ListItemButton>
                        ))}
                    </When>

                    {/* Teams Section */}
                    <When condition={searchResults.teams.length > 0}>
                        <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: GT_COLORS.headerBg }}>
                            Teams
                        </Typography>
                        {searchResults.teams.map((team) => (
                            <ListItemButton
                                key={`team-${team.id}`}
                                onClick={() => handleSelectTeam(team.id)}
                                dense
                                sx={{ '&:hover': { backgroundColor: GT_COLORS.hoverBg } }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Checkbox
                                        size="small"
                                        checked={teamIds.includes(team.id)}
                                        tabIndex={-1}
                                        disableRipple
                                        sx={{ '&.Mui-checked': { color: GT_COLORS.primary } }}
                                    />
                                </ListItemIcon>
                                <GroupIcon sx={{ mr: 1, color: GT_COLORS.textMuted, fontSize: '1rem' }} />
                                <ListItemText
                                    primary={team.name}
                                    secondary={`${team.userCount} users`}
                                />
                            </ListItemButton>
                        ))}
                    </When>

                    {/* Users Section */}
                    <When condition={searchResults.users.length > 0}>
                        <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', fontSize: '0.75rem', fontWeight: 600, color: GT_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: GT_COLORS.headerBg }}>
                            Users
                        </Typography>
                        {searchResults.users.map((user) => (
                            <ListItemButton
                                key={`user-${user.id}`}
                                onClick={() => handleSelectUser(user)}
                                dense
                                sx={{ '&:hover': { backgroundColor: GT_COLORS.hoverBg } }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Checkbox
                                        size="small"
                                        checked={userIds.includes(user.id)}
                                        tabIndex={-1}
                                        disableRipple
                                        sx={{ '&.Mui-checked': { color: GT_COLORS.primary } }}
                                    />
                                </ListItemIcon>
                                <PersonIcon sx={{ mr: 1, color: GT_COLORS.textMuted, fontSize: '0.9rem' }} />
                                <ListItemText
                                    primary={user.name}
                                    secondary={user.email}
                                />
                            </ListItemButton>
                        ))}
                    </When>
                </Paper>
            </When>

            {/* No results message */}
            <When condition={showDropdown && !hasResults && !searchLoading && debouncedQuery.length >= 1}>
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        p: 2,
                        mt: 0.5,
                        borderRadius: 2.5,
                        border: `1px solid ${GT_COLORS.border}`,
                    }}
                >
                    <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted, textAlign: 'center' }}>
                        No results found for "{debouncedQuery}"
                    </Typography>
                </Paper>
            </When>
        </Box>
    );
};
